import React, { useEffect, useMemo } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import PlacesFieldStyled from './PlacesField.styled';

const autocompleteService = { current: null, currentPlace: null };

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
}));

const PlacesField = ({
  label,
  variant,
  field: { name, value },
  mapRef,
  onChange,
  form: { errors, touched, setFieldValue },
  inputProps,
}) => {
  const classes = useStyles();
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  const fetchPlace = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.currentPlace.getDetails(request, callback);
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  useEffect(() => {
    let active = true;

    if (!autocompleteService.currentPlace && window.google.maps.places && mapRef?.current) {
      autocompleteService.currentPlace = new window.google.maps.places.PlacesService(
        mapRef?.current
      );
    }
    if (!autocompleteService.currentPlace) {
      return undefined;
    }

    if (!autocompleteService.currentPlace || !value) {
      return undefined;
    }

    if (value?.place_id) {
      fetchPlace({ placeId: value?.place_id }, (results) => {
        if (active) {
          onChange(results || {});
        }
      });
    }

    return () => {
      active = false;
    };
  }, [value]); // eslint-disable-line

  return (
    <Autocomplete
      id="google-map"
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value || null}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setFieldValue(name, newValue || '');
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <PlacesFieldStyled
          {...params}
          label={label}
          variant={variant}
          error={errors && errors[name] && touched && touched[name]}
          helperText={errors && errors[name] && touched && touched[name] && errors[name]}
          name="street-name"
          inputProps={{
            ...params.inputProps,
            ...inputProps,
          }}
        />
      )}
      renderOption={(option) => {
        const matches = option?.structured_formatting?.main_text_matched_substrings || [];
        const parts = parse(
          option?.structured_formatting?.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
          <Grid container alignItems="center">
            <Grid item>
              <LocationOnIcon className={classes.icon} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option?.structured_formatting?.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
    />
  );
};

PlacesField.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  inputProps: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

PlacesField.defaultProps = {
  required: true,
  inputProps: {},
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  field: {},
  variant: 'standard',
  label: 'Select...',
};

export default PlacesField;
